import React, { useState, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import { useAuth } from "./AuthContext";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    TimeScale
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import crosshairPlugin from "chartjs-plugin-crosshair";
import "./ZoomableChart.css";

// Custom plugin: draws OHLC candlesticks on the y1 (price) axis using raw canvas.
// Avoids chartjs-chart-financial mixed-chart incompatibilities entirely.
const candlestickOverlayPlugin = {
    id: "candlestickOverlay",
    afterDatasetsDraw(chart, _args, opts) {
        if (!opts?.data?.length) return;
        const { ctx, chartArea } = chart;
        const xs = chart.scales.x;
        const ys = chart.scales.y1;
        if (!xs || !ys) return;
        const tickW = Math.abs(xs.getPixelForValue(1) - xs.getPixelForValue(0));
        const bw = Math.max(tickW * 0.6, 1);
        ctx.save();
        ctx.beginPath();
        ctx.rect(chartArea.left, chartArea.top,
            chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
        ctx.clip();
        opts.data.forEach(({ x, o, h, l, c }) => {
            const px = xs.getPixelForValue(x);
            const color = c >= o ? "rgb(0,150,0)" : "rgb(200,0,0)";
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = 1;
            const oY = ys.getPixelForValue(o);
            const cY = ys.getPixelForValue(c);
            const hY = ys.getPixelForValue(h);
            const lY = ys.getPixelForValue(l);
            ctx.beginPath();
            ctx.moveTo(px, hY);
            ctx.lineTo(px, lY);
            ctx.stroke();
            const top = Math.min(oY, cY);
            ctx.fillRect(px - bw / 2, top, bw, Math.max(Math.abs(cY - oY), 1));
        });
        ctx.restore();
    },
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    TimeScale,
    zoomPlugin,
    crosshairPlugin,
    candlestickOverlayPlugin
);

// Recomputes y1 (price) axis bounds to match the OHLC High/Low of visible x range.
// Called on every zoom and pan event so the price axis always fits the window.
const updateY1ForVisible = ({ chart }) => {
    const ohlc = chart.options.plugins?.candlestickOverlay?.data;
    if (!ohlc?.length) return;
    const { min: xMin, max: xMax } = chart.scales.x;
    const visible = ohlc.filter(d => d.x >= Math.floor(xMin) && d.x <= Math.ceil(xMax));
    if (!visible.length) return;
    const lo = Math.min(...visible.map(d => d.l));
    const hi = Math.max(...visible.map(d => d.h));
    const pad = (hi - lo) * 0.05;
    chart.options.scales.y1.min = lo - pad;
    chart.options.scales.y1.max = hi + pad;
    chart.update("none");
};

// Colors matching momentum chart (dark blue for above SMA, red for below)
const BLUE = 'rgb(65,105,225)';  // royal blue
const RED = 'rgb(220,20,60)';    // crimson

// Calculate Simple Moving Average
const calculateSMA = (data, period) => {
    return data.map((_, i) => {
        if (i < period - 1) return null;
        const slice = data.slice(i - period + 1, i + 1).filter(v => v !== null);
        if (slice.length < period) return null;
        return slice.reduce((sum, v) => sum + v, 0) / slice.length;
    });
};

const ZoomableChart = ({ ticker = "SPX", skipAuth = false }) => {
    const [data, setData] = useState([]);
    const [timestamp, setTimestamp] = useState("");
    const [localEmail, setLocalEmail] = useState("");
    const [localError, setLocalError] = useState("");
    const chartRef = useRef(null);
    const [downloading, setDownloading] = useState(false);
    
    const auth = useAuth();
    const isAuthenticated = skipAuth ? true : auth.isAuthenticated;
    const email = skipAuth ? localEmail : (auth.email || localEmail);
    const error = skipAuth ? localError : auth.error;
    const setEmail = skipAuth ? setLocalEmail : auth.setEmail;
    const setError = skipAuth ? setLocalError : auth.setError;
    const login = auth.login;

    // Default to same-origin API if not provided via env
    const API_URL = process.env.REACT_APP_API_URL || "";

    // Ticker display names
    const tickerNames = {
        SPX: "S&P 500",
        NDX: "NASDAQ 100", 
        RUT: "Russell 2000"
    };

    // // const fetchData = useCallback(async () => {
    // //     try {
    // //         const response = await fetch(`${API_URL}/data`);
    // //         if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    // //
    // //         const { data: jsonData, timestamp: fileTimestamp } = await response.json();
    // //         setData(jsonData);
    // //         setTimestamp(fileTimestamp);
    // //     } catch (error) {
    // //         console.error("Error fetching data:", error);
    // //     }
    // }, [API_URL]);
    //
    // useEffect(() => {
    //     fetchData();
    // }, [fetchData]);

     useEffect(() => {
        let es = null;

        // Initial load
        const dataUrl = ticker === "SPX" ? `${API_URL}/data` : `${API_URL}/data?ticker=${ticker}`;
        fetch(dataUrl)
          .then(res => {
            if (!res.ok) {
              const err = `${ticker} history fetch failed: ${res.status} ${res.statusText}`;
              throw new Error(err);
            }
            return res.json();
          })
          .then(json => {
            setData(json.data);
            setTimestamp(json.timestamp);

            // Only start SSE after initial data is loaded
            setTimeout(() => {
                const streamUrl = ticker === "SPX" ? `${API_URL}/data/stream` : `${API_URL}/data/stream?ticker=${ticker}`;
                es = new EventSource(streamUrl);

                es.onmessage = (e) => {
                    const { data: newData, timestamp: ts } = JSON.parse(e.data);
                    setData(newData);
                    setTimestamp(ts);
                };

                es.onerror = () => {
                    if (es) es.close();
                };
            }, 1000);
          })
          .catch(err => {
            console.error(`Could not load ${ticker} history:`, err);
          });

        // Cleanup function
        return () => {
            if (es) es.close();
        };
    }, [API_URL, ticker]);



    // Authentication is now handled by AuthContext when skipAuth is false
    // When skipAuth is true, authentication is bypassed (for use within Models component)

    if (data.length === 0) {
        return <div className="chart-loading">Loading...</div>;
    }

    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 1);
    const twoYearsAgoStr = twoYearsAgo.toISOString().split('T')[0];

    const extraDays = 5;
    const lastDate = new Date(data[data.length - 1]?.Date);

    const futureDates = Array.from({ length: extraDays }, (_, i) => {
        const newDate = new Date(lastDate);
        newDate.setDate(newDate.getDate() + (i + 1));
        return newDate.toISOString().split("T")[0];
    });

    const extendedLabels = [...data.map((d) => d.Date), ...futureDates];
    const initialMinLabel = extendedLabels.find(l => l >= twoYearsAgoStr) || extendedLabels[0];

    const extendedIndicatorData = [
        ...data.map((d) => d.Entropy),
        ...Array(extraDays).fill(null),
    ];

    const extendedSignalData = [
        ...data.map((d) => d.Signal ?? null),
        ...Array(extraDays).fill(null),
    ];

    const labelIndexMap = new Map(extendedLabels.map((label, i) => [label, i]));
    const ohlcPluginData = data
        .filter((d) => d.Open != null && labelIndexMap.has(d.Date))
        .map((d) => ({
            x: labelIndexMap.get(d.Date),
            o: d.Open, h: d.High, l: d.Low, c: d.Close,
        }));

    // Initial visible y1 range: only data within the default 1-year window.
    const initialVisibleIdx = labelIndexMap.get(initialMinLabel) ?? 0;
    const initialVisibleOhlc = ohlcPluginData.filter(d => d.x >= initialVisibleIdx);
    const y1Min = initialVisibleOhlc.length
        ? Math.min(...initialVisibleOhlc.map(d => d.l)) * 0.99 : undefined;
    const y1Max = initialVisibleOhlc.length
        ? Math.max(...initialVisibleOhlc.map(d => d.h)) * 1.01 : undefined;

    // Calculate 21-period SMA for indicator (~1 month of market days)
    const sma21 = calculateSMA(extendedIndicatorData, 21);

    // Color based on whether value is above or below SMA
    const pointColors = extendedIndicatorData.map((val, i) => {
        if (val === null || sma21[i] === null) return BLUE;
        return val >= sma21[i] ? BLUE : RED;
    });

    const chartData = {
        labels: extendedLabels,
        datasets: [
            {
                type: "line",
                label: "Entropy",
                data: extendedIndicatorData,
                borderColor: BLUE,
                segment: {
                    borderColor: (ctx) => {
                        const idx = ctx.p1DataIndex;
                        const val = extendedIndicatorData[idx];
                        const sma = sma21[idx];
                        if (val === null || sma === null) return BLUE;
                        return val >= sma ? BLUE : RED;
                    }
                },
                pointBackgroundColor: pointColors,
                pointBorderColor: pointColors,
                borderWidth: 1.5,
                tension: 0.3,
                fill: false,
                yAxisID: "y",
                pointRadius: 1,
            },
            {
                type: "line",
                label: "Signal",
                data: extendedSignalData,
                borderColor: "rgb(128,128,128)",
                borderDash: [5, 5],
                borderWidth: 1.5,
                tension: 0.2,
                fill: false,
                yAxisID: "y2",
                pointRadius: 0,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top",
                labels: {
                    font: { size: window.innerWidth < 600 ? 10 : 14 },
                },
            },
            title: {
                display: true,
                text: `Pullback Indicator and ${tickerNames[ticker]} Close Over Time`,
                font: { size: window.innerWidth < 600 ? 14 : 20 },
            },
            tooltip: {
                mode: "index",
                intersect: false,
                position: "nearest",
                callbacks: {
                    label: (context) => {
                        const raw = context.raw;
                        if (raw != null && typeof raw === "object" && "o" in raw) {
                            return [
                                `O: ${raw.o?.toFixed(2)}`,
                                `H: ${raw.h?.toFixed(2)}`,
                                `L: ${raw.l?.toFixed(2)}`,
                                `C: ${raw.c?.toFixed(2)}`,
                            ];
                        }
                        if (raw == null) return null;
                        return `${context.dataset.label}: ${Number(raw).toFixed(2)}`;
                    },
                },
            },
            zoom: {
                pan: { enabled: true, mode: "x", onPan: updateY1ForVisible },
                zoom: {
                    wheel: { enabled: true, speed: 0.09 },
                    pinch: { enabled: true, speed: 0.09 },
                    mode: "x",
                    onZoom: updateY1ForVisible,
                },
            },
            crosshair: {
                line: { color: "rgba(0, 0, 0, 0.2)", width: 1 },
                sync: { enabled: false },
                zoom: { enabled: false },
            },
            candlestickOverlay: {
                data: ohlcPluginData,
            },
        },
        interaction: { mode: "nearest", intersect: false, axis: "x" },
        scales: {
            x: {
                min: initialMinLabel,
                title: { display: true, text: "Date" },
                offset: true,
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: 10,
                    font: { size: 12 },
                    padding: 15
                },
            },
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "Entropy" },
                grid: { drawOnChartArea: true, color: "rgba(200, 200, 200, 0.2)" },
            },
            y1: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Price" },
                grid: { drawOnChartArea: false },
                suggestedMin: y1Min,
                suggestedMax: y1Max,
            },
            y2: {
                type: "linear",
                position: "right",
                display: false,
                grid: { drawOnChartArea: false },
            },
        },
    };

    const resetZoom = () => {
        if (chartRef.current) {
            chartRef.current.resetZoom();
        }
    };

    const downloadCsv = () => {
        if (!data || data.length === 0) return;
        setDownloading(true);
        const headers = ["Date", "Entropy", "Signal", "Close"];
        const rows = data.map((d) => [
            d.Date ?? "",
            d.Entropy ?? "",
            d.Signal ?? "",
            d.Close ?? ""
        ]);
        const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${ticker}_entropy.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setDownloading(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (skipAuth) {
            // Fallback authentication for standalone use
            try {
                const response = await fetch(`${API_URL}/verify-subscriber`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email }),
                });

                const result = await response.json();
                if (response.ok && result.isSubscriber) {
                    localStorage.setItem("token", email);
                    localStorage.setItem("lastValidated", new Date().toISOString());
                    setTimeout(() => {
                        window.location.reload();
                    }, 200);
                } else {
                    setError("Access denied. Please subscribe on Substack.");
                }
            } catch {
                setError("Error verifying email.");
            }
        } else {
            // Use unified authentication system
            const success = await login(email);
            if (success) {
                setTimeout(() => {
                    window.location.reload();
                }, 200);
            }
        }
    };

    return (
        <div className="chart-wrapper">
            <div className={`chart-container ${isAuthenticated ? "authenticated" : "blurred"}`}>
                <Line ref={chartRef} data={chartData} options={options} redraw={true} />
                <p className="data-timestamp">
                    Data last updated: {timestamp}
                </p>
                <div className="chart-actions">
                    <button className="chart-reset-button" onClick={resetZoom}>
                        Reset Zoom
                    </button>
                    <button
                        className="chart-download-button"
                        onClick={downloadCsv}
                        disabled={downloading || data.length === 0}
                    >
                        {downloading ? "Preparing..." : "Download CSV"}
                    </button>
                </div>
            </div>

            {!isAuthenticated && (
                <div className="login-overlay">
                    <h2>Subscriber Login</h2>
                    <p>Enter your email to access the site.</p>
                    <form onSubmit={handleSubmit}>
                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        <button type="submit">Login</button>
                    </form>
                    {error && <p className="error-message">{error}</p>}

                    <iframe
                        src="https://machinaquanta.substack.com/embed"
                        title="Substack Subscription Login"
                        width="480"
                        height="320"
                        frameBorder="0"
                        scrolling="no"
                    ></iframe>
                </div>
            )}
        </div>
    );
};

export default ZoomableChart;
